import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Plus, ShoppingCart, Truck, Package, X, Check, RotateCcw, ChevronLeft, ChevronRight, Search, Minus, Link, ScanLine } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const orderStatusVariants = {
    PENDING: 'warning',
    CONFIRMED: 'info',
    DISPATCHED: 'purple',
    DELIVERED: 'success',
    RETURNED: 'danger',
    CANCELLED: 'secondary'
};

const TUNISIAN_GOVERNORATES = [
    'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
    'Kairouan', 'Kasserine', 'Kebili', 'Kef', 'Mahdia', 'Manouba', 'Medenine',
    'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana', 'Sousse',
    'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan'
];

export function OrdersPage() {
    const { t, language } = useLanguage();
    const [orders, setOrders] = useState([]);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');

    // Assign Products dialog state
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [assignOrderId, setAssignOrderId] = useState(null);
    const [assignStep, setAssignStep] = useState(1);

    // QR Scanner state
    const [scanDialogOpen, setScanDialogOpen] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const html5QrCodeRef = useRef(null);

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [productSearch, setProductSearch] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [pricingMode, setPricingMode] = useState('individual');
    const [totalPrice, setTotalPrice] = useState('');
    const [customerInfo, setCustomerInfo] = useState({
        customerName: '',
        customerPhone: '',
        customerAddress: '',
        city: 'Tunis',
        subDivision: '',
        deliveryFee: '8'
    });

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

            const [ordersRes, inventoryRes] = await Promise.all([
                axios.get(`${API_URL}/orders?${params}`, { headers }),
                axios.get(`${API_URL}/inventory?status=AVAILABLE`, { headers })
            ]);
            setOrders(ordersRes.data.data.orders);
            setAvailableUnits(inventoryRes.data.data.units);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const groupedUnits = useMemo(() => {
        const groups = {};
        availableUnits.forEach(unit => {
            const productId = unit.product?.id || 'unknown';
            if (!groups[productId]) {
                groups[productId] = { product: unit.product, units: [] };
            }
            groups[productId].units.push(unit);
        });
        return Object.values(groups);
    }, [availableUnits]);

    const filteredGroups = useMemo(() => {
        if (!productSearch) return groupedUnits;
        const lowerSearch = productSearch.toLowerCase();
        return groupedUnits.filter(g =>
            g.product?.name?.toLowerCase().includes(lowerSearch) ||
            g.product?.sku?.toLowerCase().includes(lowerSearch)
        );
    }, [groupedUnits, productSearch]);

    const resetWizard = () => {
        setCurrentStep(1);
        setProductSearch('');
        setSelectedItems([]);
        setPricingMode('individual');
        setTotalPrice('');
        setCustomerInfo({
            customerName: '',
            customerPhone: '',
            customerAddress: '',
            city: 'Tunis',
            subDivision: '',
            deliveryFee: '8'
        });
    };

    const openDialog = () => {
        resetWizard();
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        resetWizard();
    };

    const toggleUnitSelection = (unit) => {
        const existing = selectedItems.find(i => i.unitId === unit.id);
        if (existing) {
            setSelectedItems(selectedItems.filter(i => i.unitId !== unit.id));
        } else {
            setSelectedItems([...selectedItems, {
                unitId: unit.id,
                productName: unit.product?.name || 'Unknown',
                sku: unit.product?.sku || '',
                unitUuid: unit.unitUuid,
                buyPrice: parseFloat(unit.buyPrice),
                sellPrice: ''
            }]);
        }
    };

    const updateItemPrice = (unitId, price) => {
        setSelectedItems(selectedItems.map(item =>
            item.unitId === unitId ? { ...item, sellPrice: price } : item
        ));
    };

    const applyTotalPrice = () => {
        if (!totalPrice || selectedItems.length === 0) return;
        const pricePerUnit = (parseFloat(totalPrice) / selectedItems.length).toFixed(2);
        setSelectedItems(selectedItems.map(item => ({ ...item, sellPrice: pricePerUnit })));
    };

    const calculateSubtotal = () => {
        return selectedItems.reduce((sum, item) => sum + (parseFloat(item.sellPrice) || 0), 0);
    };

    const handleSubmit = async () => {
        try {
            const items = selectedItems.map(item => ({
                unitId: item.unitId,
                sellPrice: item.sellPrice
            }));
            await axios.post(`${API_URL}/orders`, { ...customerInfo, items }, { headers });
            closeDialog();
            fetchData();
        } catch (error) {
            console.error('Failed to create order:', error);
            toast.error(error.response?.data?.error || 'Failed to create order');
        }
    };

    const handleDispatch = async (orderId) => {
        try {
            await axios.patch(`${API_URL}/orders/${orderId}/dispatch`, { useDeliveryProvider: true }, { headers });
            fetchData();
        } catch (error) {
            console.error('Failed to dispatch:', error);
            toast.error(error.response?.data?.error || 'Failed to dispatch order');
        }
    };

    const handleDeliver = async (orderId) => {
        try {
            await axios.patch(`${API_URL}/orders/${orderId}/deliver`, {}, { headers });
            fetchData();
        } catch (error) {
            console.error('Failed to deliver:', error);
            toast.error(error.response?.data?.error || 'Failed to mark as delivered');
        }
    };

    const handleReturn = async (orderId) => {
        try {
            await axios.patch(`${API_URL}/orders/${orderId}/return`, {}, { headers });
            fetchData();
        } catch (error) {
            console.error('Failed to return:', error);
            toast.error(error.response?.data?.error || 'Failed to mark as returned');
        }
    };

    const handleCancel = async (orderId) => {
        if (!window.confirm(t('confirmCancel'))) return;
        try {
            await axios.delete(`${API_URL}/orders/${orderId}`, { headers });
            fetchData();
        } catch (error) {
            console.error('Failed to cancel:', error);
            toast.error(error.response?.data?.error || 'Failed to cancel order');
        }
    };

    const openAssignDialog = (orderId) => {
        setAssignOrderId(orderId);
        setAssignStep(1);
        setProductSearch('');
        setSelectedItems([]);
        setPricingMode('individual');
        setTotalPrice('');
        setAssignDialogOpen(true);
    };

    const handleAssignProducts = async () => {
        try {
            const items = selectedItems.map(item => ({
                unitId: item.unitId,
                sellPrice: item.sellPrice
            }));
            await axios.patch(`${API_URL}/orders/${assignOrderId}/assign-products`, { items }, { headers });
            setAssignDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to assign products:', error);
            toast.error(error.response?.data?.error || 'Failed to assign products');
        }
    };

    const startScanner = useCallback(async () => {
        setScanResult(null);
        setScanning(true);
        try {
            const html5QrCode = new Html5Qrcode('qr-reader');
            html5QrCodeRef.current = html5QrCode;
            await html5QrCode.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                async (decodedText) => {
                    try { await html5QrCode.stop(); } catch (e) { /* ignore */ }
                    html5QrCodeRef.current = null;
                    setScanning(false);
                    try {
                        const res = await axios.post(`${API_URL}/orders/scan-dispatch`, { trackingId: decodedText }, { headers });
                        setScanResult({
                            success: true,
                            message: `Order ${res.data.data.order.orderNumber} dispatched!`,
                            order: res.data.data.order
                        });
                        fetchData();
                    } catch (error) {
                        setScanResult({
                            success: false,
                            message: error.response?.data?.error || 'Failed to dispatch order'
                        });
                    }
                },
                () => { /* ignore scan errors */ }
            );
        } catch (error) {
            setScanning(false);
            setScanResult({ success: false, message: 'Could not access camera: ' + error.message });
        }
    }, [headers]);

    const stopScanner = useCallback(async () => {
        if (html5QrCodeRef.current) {
            try { await html5QrCodeRef.current.stop(); } catch (e) { /* ignore */ }
            html5QrCodeRef.current = null;
        }
        setScanning(false);
    }, []);

    const closeScanDialog = useCallback(async () => {
        await stopScanner();
        setScanDialogOpen(false);
        setScanResult(null);
    }, [stopScanner]);

    const handleSyncIntigo = async () => {
        setIsSyncing(true);
        try {
            const res = await axios.post(`${API_URL}/orders/sync-intigo`, {}, { headers });
            toast.success(t('syncSuccess') || `Sync complete: ${res.data.data.created} created, ${res.data.data.updated} updated`);
            fetchData();
        } catch (error) {
            console.error('Failed to sync Intigo:', error);
            toast.error(error.response?.data?.error || t('syncFailed') || 'Failed to sync with Intigo');
        } finally {
            setIsSyncing(false);
        }
    };

    const openScanDialog = () => {
        setScanResult(null);
        setScanning(false);
        setScanDialogOpen(true);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US');
    };

    const formatCurrency = (amount) => {
        return `${parseFloat(amount || 0).toFixed(2)} TND`;
    };

    const canProceedStep1 = selectedItems.length > 0;
    const canProceedStep2 = selectedItems.every(item => item.sellPrice && parseFloat(item.sellPrice) > 0);
    const canProceedStep3 = customerInfo.customerName && customerInfo.customerPhone && customerInfo.customerAddress;

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    const renderStep1 = () => (
        <div className="space-y-4">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder={t('searchProducts')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-9"
                />
            </div>
            <div className="max-h-[40vh] overflow-y-auto space-y-2">
                {filteredGroups.map(group => (
                    <Card key={group.product?.id || 'unknown'} className="overflow-hidden">
                        <CardHeader className="py-2 px-4 bg-muted/50">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-sm">{group.product?.name}</CardTitle>
                                    <CardDescription className="text-xs">{group.product?.sku} • {group.units.length} {t('available')}</CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {group.units.map(unit => {
                                const isSelected = selectedItems.some(i => i.unitId === unit.id);
                                return (
                                    <div
                                        key={unit.id}
                                        onClick={() => toggleUnitSelection(unit)}
                                        className={`flex items-center justify-between px-4 py-2 border-t cursor-pointer hover:bg-muted/30 transition-colors ${isSelected ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                                            </div>
                                            <span className="font-mono text-xs text-muted-foreground">{unit.unitUuid}</span>
                                        </div>
                                        <span className="text-sm">{formatCurrency(unit.buyPrice)}</span>
                                    </div>
                                );
                            })}
                        </CardContent>
                    </Card>
                ))}
                {filteredGroups.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        {t('noProductsFound')}
                    </div>
                )}
            </div>
            <div className="text-sm text-muted-foreground text-center">
                {selectedItems.length} {t('itemsSelected')}
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-4">
            <div className="flex gap-2 mb-4">
                <Button variant={pricingMode === 'individual' ? 'default' : 'outline'} size="sm" onClick={() => setPricingMode('individual')}>
                    {t('individualPricing')}
                </Button>
                <Button variant={pricingMode === 'total' ? 'default' : 'outline'} size="sm" onClick={() => setPricingMode('total')}>
                    {t('totalPricing')}
                </Button>
            </div>

            {pricingMode === 'total' && (
                <div className="flex gap-2 items-end mb-4 p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                        <Label>{t('totalOrderPrice')}</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={totalPrice}
                            onChange={(e) => setTotalPrice(e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                    <Button onClick={applyTotalPrice} disabled={!totalPrice}>{t('apply')}</Button>
                </div>
            )}

            <div className="max-h-[35vh] overflow-y-auto space-y-2">
                {selectedItems.map(item => (
                    <div key={item.unitId} className="flex items-center gap-3 p-3 border rounded-lg">
                        <div className="flex-1">
                            <div className="font-medium text-sm">{item.productName}</div>
                            <div className="text-xs text-muted-foreground">{item.sku} • {item.unitUuid}</div>
                        </div>
                        <div className="w-32">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.sellPrice}
                                onChange={(e) => updateItemPrice(item.unitId, e.target.value)}
                                placeholder={t('sellPrice')}
                                className="text-right"
                            />
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setSelectedItems(selectedItems.filter(i => i.unitId !== item.unitId))}>
                            <Minus className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                ))}
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
                <span className="text-muted-foreground">{t('subtotal')}</span>
                <span className="text-lg font-bold">{formatCurrency(calculateSubtotal())}</span>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="customerName">{t('customerName')}</Label>
                    <Input
                        id="customerName"
                        value={customerInfo.customerName}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, customerName: e.target.value })}
                        required
                    />
                </div>
                <div>
                    <Label htmlFor="customerPhone">{t('customerPhone')}</Label>
                    <Input
                        id="customerPhone"
                        value={customerInfo.customerPhone}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, customerPhone: e.target.value })}
                        required
                    />
                </div>
            </div>
            <div>
                <Label htmlFor="customerAddress">{t('customerAddress')}</Label>
                <Input
                    id="customerAddress"
                    value={customerInfo.customerAddress}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, customerAddress: e.target.value })}
                    required
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="city">{t('governorate')}</Label>
                    <Select value={customerInfo.city} onValueChange={(value) => setCustomerInfo({ ...customerInfo, city: value })}>
                        <SelectTrigger>
                            <SelectValue placeholder={t('selectGovernorate')} />
                        </SelectTrigger>
                        <SelectContent>
                            {TUNISIAN_GOVERNORATES.map(gov => (
                                <SelectItem key={gov} value={gov}>{gov}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor="subDivision">{t('district')}</Label>
                    <Input
                        id="subDivision"
                        value={customerInfo.subDivision}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, subDivision: e.target.value })}
                        placeholder="e.g. Houmt Souk"
                    />
                </div>
            </div>
            <div>
                <Label htmlFor="deliveryFee">{t('deliveryFee')}</Label>
                <Input
                    id="deliveryFee"
                    type="number"
                    step="0.5"
                    min="0"
                    value={customerInfo.deliveryFee}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, deliveryFee: e.target.value })}
                    placeholder="Default: 8"
                />
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-4">
            <Card>
                <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                        {t('orderItems')}
                        <Button variant="ghost" size="sm" onClick={() => setCurrentStep(1)}>{t('edit')}</Button>
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                    {selectedItems.map(item => (
                        <div key={item.unitId} className="flex justify-between">
                            <span>{item.productName} <span className="text-muted-foreground">({item.sku})</span></span>
                            <span>{formatCurrency(item.sellPrice)}</span>
                        </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t font-medium">
                        <span>{t('subtotal')}</span>
                        <span>{formatCurrency(calculateSubtotal())}</span>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                        {t('customerInfo')}
                        <Button variant="ghost" size="sm" onClick={() => setCurrentStep(3)}>{t('edit')}</Button>
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                    <div><strong>{t('customerName')}:</strong> {customerInfo.customerName}</div>
                    <div><strong>{t('customerPhone')}:</strong> {customerInfo.customerPhone}</div>
                    <div><strong>{t('customerAddress')}:</strong> {customerInfo.customerAddress}</div>
                    <div><strong>{t('governorate')}:</strong> {customerInfo.city}</div>
                    {customerInfo.subDivision && <div><strong>{t('district')}:</strong> {customerInfo.subDivision}</div>}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="py-3">
                    <CardTitle className="text-sm">{t('orderTotal')}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                    <div className="flex justify-between">
                        <span>{t('subtotal')}</span>
                        <span>{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>{t('deliveryFee')}</span>
                        <span>{formatCurrency(customerInfo.deliveryFee)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t text-lg font-bold">
                        <span>{t('total')}</span>
                        <span>{formatCurrency(calculateSubtotal() + parseFloat(customerInfo.deliveryFee || 0))}</span>
                    </div>
                </CardContent>
            </Card>
        </div>
    );

    const stepTitles = [t('selectProducts'), t('setPrices'), t('customerDetails'), t('confirmation')];

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">{t('orders')}</h1>
                        <p className="text-muted-foreground">{t('ordersSubtitle')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleSyncIntigo} disabled={isSyncing}>
                            <RotateCcw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                            {isSyncing ? (t('syncing') || 'Syncing...') : (t('syncIntigo') || 'Sync Intigo')}
                        </Button>
                        <Button variant="outline" onClick={openScanDialog}>
                            <ScanLine className="h-4 w-4 mr-2" />
                            {t('scanQR') || 'Scan QR'}
                        </Button>
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild>
                                <Button disabled={availableUnits.length === 0} onClick={openDialog}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('newOrder')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                                <DialogHeader>
                                    <DialogTitle>{t('createNewOrder')}</DialogTitle>
                                    <DialogDescription>
                                        {t('step')} {currentStep} {t('of')} 4: {stepTitles[currentStep - 1]}
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="flex gap-1 mb-4">
                                    {[1, 2, 3, 4].map(step => (
                                        <div
                                            key={step}
                                            className={`h-1 flex-1 rounded transition-colors ${step <= currentStep ? 'bg-primary' : 'bg-muted'}`}
                                        />
                                    ))}
                                </div>

                                <div className="flex-1 overflow-y-auto py-2">
                                    {currentStep === 1 && renderStep1()}
                                    {currentStep === 2 && renderStep2()}
                                    {currentStep === 3 && renderStep3()}
                                    {currentStep === 4 && renderStep4()}
                                </div>

                                <DialogFooter className="flex justify-between gap-2 pt-4 border-t">
                                    <div>
                                        {currentStep > 1 && (
                                            <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                                                <ChevronLeft className="h-4 w-4 mr-1" />
                                                {t('back')}
                                            </Button>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" onClick={closeDialog}>{t('cancel')}</Button>
                                        {currentStep < 4 ? (
                                            <Button
                                                onClick={() => setCurrentStep(currentStep + 1)}
                                                disabled={
                                                    (currentStep === 1 && !canProceedStep1) ||
                                                    (currentStep === 2 && !canProceedStep2) ||
                                                    (currentStep === 3 && !canProceedStep3)
                                                }
                                            >
                                                {t('next')}
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        ) : (
                                            <Button onClick={handleSubmit}>
                                                <Check className="h-4 w-4 mr-1" />
                                                {t('createOrder')}
                                            </Button>
                                        )}
                                    </div>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Filter */}
                <div className="flex gap-4 items-center">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder={t('allStatuses')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('allStatuses')}</SelectItem>
                            {Object.keys(orderStatusVariants).map((status) => (
                                <SelectItem key={status} value={status}>{t(status.toLowerCase())}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Orders Table */}
                <div className="border rounded-lg">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('orderNumber')}</TableHead>
                                <TableHead>{t('customerName')}</TableHead>
                                <TableHead>{t('customerPhone')}</TableHead>
                                <TableHead>{t('description') || 'Description'}</TableHead>
                                <TableHead>{t('items')}</TableHead>
                                <TableHead>{t('totalPrice') || 'Total Price'}</TableHead>
                                <TableHead>{t('status')}</TableHead>
                                <TableHead>{t('created')}</TableHead>
                                <TableHead className="text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                        <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        {t('noOrders')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                orders.map((order) => (
                                    <TableRow key={order.id}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm">{order.orderNumber}</span>
                                                {order.source === 'INTIGO' && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-600">INTIGO</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium">{order.customerName}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm text-muted-foreground">{order.customerPhone}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                {order.description ? order.description : <span className="text-muted-foreground italic">—</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                {order.orderItems?.length > 0 ? (
                                                    t('unitCount').replace('{0}', order.orderItems.length)
                                                ) : (
                                                    <span className="text-muted-foreground italic">—</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                {order.totalAmount ? (
                                                    <span>{formatCurrency(order.totalAmount)}</span>
                                                ) : (
                                                    <span className="text-muted-foreground italic">—</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={orderStatusVariants[order.status]}>
                                                {t(order.status.toLowerCase())}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {formatDate(order.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {order.orderItems?.length === 0 && (
                                                    <Button variant="ghost" size="icon" title={t('assignProducts') || 'Assign Products'} onClick={() => openAssignDialog(order.id)}>
                                                        <Link className="h-4 w-4 text-orange-500" />
                                                    </Button>
                                                )}
                                                {(order.status === 'PENDING' || order.status === 'CONFIRMED') && (
                                                    <>
                                                        <Button variant="ghost" size="icon" title={t('dispatch')} onClick={() => handleDispatch(order.id)}>
                                                            <Truck className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" title={t('cancel')} onClick={() => handleCancel(order.id)}>
                                                            <X className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </>
                                                )}
                                                {order.status === 'DISPATCHED' && (
                                                    <>
                                                        <Button variant="ghost" size="icon" title={t('markDelivered')} onClick={() => handleDeliver(order.id)}>
                                                            <Check className="h-4 w-4 text-green-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" title={t('markReturned')} onClick={() => handleReturn(order.id)}>
                                                            <RotateCcw className="h-4 w-4 text-purple-500" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Assign Products Dialog */}
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t('assignProducts') || 'Assign Products'}</DialogTitle>
                        <DialogDescription>
                            {t('step')} {assignStep} {t('of')} 2: {assignStep === 1 ? (t('selectProducts') || 'Select Products') : (t('setPrices') || 'Set Prices')}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex gap-1 mb-4">
                        {[1, 2].map(step => (
                            <div
                                key={step}
                                className={`h-1 flex-1 rounded transition-colors ${step <= assignStep ? 'bg-primary' : 'bg-muted'}`}
                            />
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto py-2">
                        {assignStep === 1 && renderStep1()}
                        {assignStep === 2 && renderStep2()}
                    </div>

                    <DialogFooter className="flex justify-between gap-2 pt-4 border-t">
                        <div>
                            {assignStep > 1 && (
                                <Button variant="outline" onClick={() => setAssignStep(1)}>
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    {t('back')}
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => setAssignDialogOpen(false)}>{t('cancel')}</Button>
                            {assignStep === 1 ? (
                                <Button onClick={() => setAssignStep(2)} disabled={!canProceedStep1}>
                                    {t('next')}
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            ) : (
                                <Button onClick={handleAssignProducts} disabled={!canProceedStep2}>
                                    <Check className="h-4 w-4 mr-1" />
                                    {t('assignProducts') || 'Assign Products'}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR Scanner Dialog */}
            <Dialog open={scanDialogOpen} onOpenChange={(open) => { if (!open) closeScanDialog(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ScanLine className="h-5 w-5" />
                            {t('scanQR') || 'Scan QR Code'}
                        </DialogTitle>
                        <DialogDescription>
                            {t('scanQRDesc') || 'Scan the QR code on the bordereau to dispatch the order'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div id="qr-reader" className="w-full rounded-lg overflow-hidden bg-muted min-h-[300px]" />

                        {!scanning && !scanResult && (
                            <Button className="w-full" onClick={startScanner}>
                                <ScanLine className="h-4 w-4 mr-2" />
                                {t('startScanning') || 'Start Scanning'}
                            </Button>
                        )}

                        {scanResult && (
                            <div className={`p-4 rounded-lg border ${scanResult.success
                                ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200'
                                : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
                                }`}>
                                <div className="flex items-center gap-2 font-medium">
                                    {scanResult.success ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                                    {scanResult.message}
                                </div>
                                {scanResult.order && (
                                    <div className="mt-2 text-sm opacity-80">
                                        {scanResult.order.customerName} — {scanResult.order.customerPhone}
                                    </div>
                                )}
                            </div>
                        )}

                        {scanResult && (
                            <div className="flex gap-2">
                                <Button variant="outline" className="flex-1" onClick={() => { setScanResult(null); startScanner(); }}>
                                    {t('scanAnother') || 'Scan Another'}
                                </Button>
                                <Button variant="ghost" className="flex-1" onClick={closeScanDialog}>
                                    {t('close') || 'Close'}
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
